import { useEffect, useState } from 'react';
import type { EnginePromotion } from '@shared/pricing/promotion-engine';
import {
  getOfflineCachedLocations,
  getOfflineCachedPromotions,
  getOfflineCachedTenantSettings,
  type CachedLocation,
  type CachedSettings,
} from '@/lib/offline-db';

export interface OfflinePricingData {
  promotions: EnginePromotion[];
  locations: CachedLocation[];
  tenant: CachedSettings | null;
  /** True once the cache has been read (it can still be empty). */
  loaded: boolean;
}

const EMPTY: OfflinePricingData = { promotions: [], locations: [], tenant: null, loaded: false };

/**
 * Reads what the till needs to price a cart with no connection (POS-I78) from the offline cache:
 * the automatic promotions, the locations (ATL / further tax / payment-method tax settings) and
 * the tenant tax-rule settings. Loads only while `active` (offline and something to price).
 */
export function useOfflinePricingData(active: boolean): OfflinePricingData {
  const [data, setData] = useState<OfflinePricingData>(EMPTY);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void Promise.all([
      getOfflineCachedPromotions(),
      getOfflineCachedLocations(),
      getOfflineCachedTenantSettings(),
    ])
      .then(([promotions, locations, tenant]) => {
        if (cancelled) return;
        setData({ promotions: promotions ?? [], locations: locations ?? [], tenant: tenant ?? null, loaded: true });
      })
      .catch(() => {
        if (!cancelled) setData({ ...EMPTY, loaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return data;
}
